import React, { useState, useEffect, useRef } from 'react';
import { fetchGalleries, fetchGalleriBilleder, parseImageCaptions, fetchSenesteProjekter } from '../lib/cms';
import AlbumModal from './AlbumModal';

const MAIN_PAGES = [
  { id: 'alle', title: 'Alle projekter', icon: 'fa-solid fa-shapes' },
  { id: 'dekoration', title: 'Dekoration', icon: 'fa-solid fa-car' },
  { id: 'solfilm', title: 'Solfilm', icon: 'fa-solid fa-sun' },
  { id: 'interioerfolie', title: 'Interiør folie', icon: 'fa-solid fa-couch' },
  { id: 'logodesign', title: 'Logo design', icon: 'fa-solid fa-pen-nib' }
];

function getOptimizedImageUrl(url, width = 700) {
  if (!url) return '';
  if (url.startsWith('https://assets.auxodev.dk/') && !url.includes('/cdn-cgi/image/')) {
    return `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/${url.replace('https://assets.auxodev.dk/', '')}`;
  }
  return url;
}

function getGalleryPage(gallery) {
  const termCat = gallery.terms?.['galleri-kategori']?.[0];
  if (termCat === 'dekoration') return { id: 'dekoration', title: 'Dekoration' };
  if (termCat === 'solfilm') return { id: 'solfilm', title: 'Solfilm' };
  if (termCat === 'interioerfolie') return { id: 'interioerfolie', title: 'Interiør folie' };
  if (termCat === 'logodesign') return { id: 'logodesign', title: 'Logo design' };

  const slug = (gallery.page_slug || gallery.side_slug || '').toLowerCase();
  if (slug.includes('dekoration')) return { id: 'dekoration', title: 'Dekoration' };
  if (slug.includes('solfilm')) return { id: 'solfilm', title: 'Solfilm' };
  if (slug.includes('interioer')) return { id: 'interioerfolie', title: 'Interiør folie' };
  if (slug.includes('logo')) return { id: 'logodesign', title: 'Logo design' };

  return { id: 'dekoration', title: 'Dekoration' };
}

export default function ProjectAlbums() {
  const [projekter, setProjekter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState('alle');
  const [activeSubCategory, setActiveSubCategory] = useState('alle');
  const [showSubCategories, setShowSubCategories] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);

  const scrollContainerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const dragDistanceRef = useRef(0);
  const [isPointerDown, setIsPointerDown] = useState(false);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      // 1. Hent først fra den dedikerede samling 'seneste_projekter'
      const seneste = await fetchSenesteProjekter();
      if (seneste && seneste.length > 0) {
        setProjekter(seneste);
        setLoading(false);
        return;
      }

      // 2. Fallback hvis samlingen endnu ikke er udfyldt
      try {
        const [data, allBilleder] = await Promise.all([
          fetchGalleries(),
          fetchGalleriBilleder()
        ]);
        const fallbackList = [];
        if (allBilleder && allBilleder.length > 0) {
          allBilleder.forEach((b, idx) => {
            if (!b.url) return;
            const matchingGallery = (data || []).find((g) => g.side_slug === b.galleri_slug || g.page_slug === b.galleri_slug);
            const pageInfo = matchingGallery ? getGalleryPage(matchingGallery) : { id: 'dekoration', title: 'Dekoration' };
            const subTitle = b.kategori || matchingGallery?.title || pageInfo.title;
            const imgs = (b.images && b.images.length > 0) ? b.images : (b.url ? [b.url] : []);
            fallbackList.push({
              id: b.id || `fallback-${idx}`,
              imageUrl: b.url,
              url: b.url,
              images: imgs,
              imageCount: imgs.length,
              isAlbum: imgs.length > 1,
              mainPageId: pageInfo.id,
              mainPageTitle: pageInfo.title,
              categorySlug: b.galleri_slug || pageInfo.id,
              subCategorySlug: (b.galleri_slug || subTitle).toLowerCase().replace(/[^a-z0-9]+/g, '-'),
              categoryTitle: subTitle,
              subCategoryTitle: subTitle,
              galleryTitle: b.galleri_titel || matchingGallery?.title || pageInfo.title,
              imageTitle: b.titel || 'CRE8IT Projekt',
              imageDescription: b.beskrivelse || '',
              badgeText: subTitle,
              description: b.beskrivelse || '',
              sortering: b.sortering || idx
            });
          });
        }
        setProjekter(fallbackList);
      } catch (err) {
        console.warn('Fallback indlæsning fejlede:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const allProjects = projekter;

  // Beregn antal billeder pr. side
  const pageCounts = React.useMemo(() => {
    const counts = { alle: allProjects.length };
    MAIN_PAGES.forEach((p) => {
      if (p.id !== 'alle') {
        counts[p.id] = allProjects.filter((item) => item.mainPageId === p.id).length;
      }
    });
    return counts;
  }, [allProjects]);

  // Beregn tilgængelige underkategorier baseret på den valgte side
  const availableSubCategories = React.useMemo(() => {
    const subMap = new Map();

    allProjects.forEach((p) => {
      // Hvis en specifik side er valgt, medtag kun denne sides underkategorier
      if (activePage !== 'alle' && p.mainPageId !== activePage) {
        return;
      }

      const slug = p.subCategorySlug || p.categorySlug;
      const label = p.subCategoryTitle || p.categoryTitle;

      if (slug && label && label.toLowerCase() !== (p.mainPageTitle || '').toLowerCase()) {
        if (!subMap.has(slug)) {
          subMap.set(slug, {
            slug,
            label,
            count: 1,
            pageId: p.mainPageId,
            pageTitle: p.mainPageTitle
          });
        } else {
          subMap.get(slug).count += 1;
        }
      }
    });

    return Array.from(subMap.values());
  }, [allProjects, activePage]);

  // Filtrerede billeder til visning
  const filteredProjects = React.useMemo(() => {
    let list = allProjects;
    if (activePage !== 'alle') {
      list = list.filter((p) => p.mainPageId === activePage);
    }
    if (activeSubCategory !== 'alle') {
      list = list.filter(
        (p) => p.categorySlug === activeSubCategory || p.subCategorySlug === activeSubCategory
      );
    }
    return list;
  }, [allProjects, activePage, activeSubCategory]);

  // Nulstil scroll-position og lightbox ved filterskift
  const handlePageChange = (pageId) => {
    setActivePage(pageId);
    setActiveSubCategory('alle');
    setLightboxIndex(null);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  const handleSubCategoryChange = (subSlug) => {
    setActiveSubCategory(subSlug);
    setLightboxIndex(null);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  // Horisontal scroll funktioner
  const scroll = (direction) => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, clientWidth } = scrollContainerRef.current;
    const scrollAmount = clientWidth * 0.75;
    scrollContainerRef.current.scrollTo({
      left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
      behavior: 'smooth'
    });
  };

  // Træk / Drag-to-scroll handlers til mus
  const handleMouseDown = (e) => {
    if (!scrollContainerRef.current) return;
    if (e.button !== 0) return; // Kun primær museknap
    isDraggingRef.current = true;
    dragDistanceRef.current = 0;
    setIsPointerDown(true);
    startXRef.current = e.pageX;
    startYRef.current = e.pageY;
    scrollLeftRef.current = scrollContainerRef.current.scrollLeft;
  };

  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (!isDraggingRef.current || !scrollContainerRef.current) return;
      const dx = e.pageX - startXRef.current;
      const dy = e.pageY - startYRef.current;
      const dist = Math.hypot(dx, dy);
      dragDistanceRef.current = Math.max(dragDistanceRef.current, dist);

      // Scroll jævnt med trækket
      scrollContainerRef.current.scrollLeft = scrollLeftRef.current - dx;
    };

    const handleGlobalMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        setIsPointerDown(false);
        // Nulstil efter et kort øjeblik, så en igangværende click-event kan nå at registrere om det var et træk
        setTimeout(() => {
          dragDistanceRef.current = 0;
        }, 120);
      }
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-red border-t-transparent" />
      </div>
    );
  }

  if (allProjects.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* 1. Sortering efter side & videre filtrering efter underkategorier */}
      <div className="space-y-3">
        {/* Hovedkategorier og filtreringsknap på 1 linje */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 sm:gap-3">
          {/* Niveau 1: Vælg side (Hovedkategori) */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
            {MAIN_PAGES.map((page) => {
              const isActive = activePage === page.id;
              const count = pageCounts[page.id] || 0;
              if (page.id !== 'alle' && count === 0) return null;

              return (
                <button
                  key={page.id}
                  type="button"
                  onClick={() => handlePageChange(page.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs sm:text-sm font-bold transition-all cursor-pointer shadow-2xs ${
                    isActive
                      ? 'bg-[#325d52] text-white shadow-md ring-2 ring-[#325d52]/25'
                      : 'bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-950 border border-gray-200/90'
                  }`}
                >
                  <i className={`${page.icon} text-xs ${isActive ? 'text-[#deecd4]' : 'text-gray-400'}`} />
                  <span>{page.title}</span>
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${
                      isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Niveau 2: Videre filtrering efter underkategorier på den valgte side */}
          {availableSubCategories.length > 0 && (
            <div className="flex items-center justify-end gap-2 flex-wrap sm:shrink-0 sm:self-center">
              {/* Hurtig nulstilling hvis en underkategori er aktiv */}
              {activeSubCategory !== 'alle' && (
                <button
                  type="button"
                  onClick={() => handleSubCategoryChange('alle')}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600 px-2 py-1 transition-colors cursor-pointer mr-1"
                >
                  <i className="fa-solid fa-xmark text-[11px]" />
                  <span>Nulstil underkategori</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => setShowSubCategories((prev) => !prev)}
                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer border shadow-2xs ${
                  showSubCategories || activeSubCategory !== 'alle'
                    ? 'bg-emerald-50/70 text-[#245b53] border-[#325d52]/30 shadow-xs'
                    : 'bg-white text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-gray-200/90'
                }`}
              >
                <i className="fa-solid fa-sliders text-xs text-[#325d52]" />
                <span>
                  {activeSubCategory !== 'alle'
                    ? `Underkategori: ${availableSubCategories.find((s) => s.slug === activeSubCategory)?.label || activeSubCategory}`
                    : 'Filtrér efter underkategori'}
                </span>
                <span
                  className={`text-[11px] px-1.5 py-0.2 rounded-full font-medium ${
                    activeSubCategory !== 'alle'
                      ? 'bg-[#325d52] text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {availableSubCategories.length}
                </span>
                <i
                  className={`fa-solid fa-chevron-down text-[10px] text-gray-400 transition-transform duration-200 ${
                    showSubCategories ? 'rotate-180 text-[#325d52]' : ''
                  }`}
                />
              </button>
            </div>
          )}
        </div>

        {/* Selve underkategori-containeren – vises kun når brugeren folder den ud */}
        {availableSubCategories.length > 0 && showSubCategories && (
              <div className="relative flex flex-wrap items-center gap-1.5 sm:gap-2 p-2.5 sm:p-3 rounded-2xl bg-gray-50/90 border border-gray-200/80 transition-all duration-200">
                <div className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider px-2 py-1">
                  <i className="fa-solid fa-filter text-[11px] text-[#325d52]" />
                  <span>Underkategori:</span>
                </div>

                {/* Knap til alle på denne side */}
                <button
                  type="button"
                  onClick={() => handleSubCategoryChange('alle')}
                  className={`px-3 py-1.5 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                    activeSubCategory === 'alle'
                      ? 'bg-[#325d52] text-white shadow-xs'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {activePage === 'alle'
                    ? 'Alle underkategorier'
                    : `Alle i ${MAIN_PAGES.find((p) => p.id === activePage)?.title || ''}`}
                  <span
                    className={`ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full ${
                      activeSubCategory === 'alle' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {pageCounts[activePage] || allProjects.length}
                  </span>
                </button>

                {/* De specifikke underkategorier */}
                {availableSubCategories.map((sub) => {
                  const isActive = activeSubCategory === sub.slug;
                  return (
                    <button
                      key={sub.slug}
                      type="button"
                      onClick={() => {
                        if (activePage === 'alle') {
                          setActivePage(sub.pageId);
                        }
                        handleSubCategoryChange(sub.slug);
                      }}
                      className={`px-3 py-1.5 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                        isActive
                          ? 'bg-[#325d52] text-white shadow-xs ring-1 ring-[#325d52]'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      <span>{sub.label}</span>
                      <span
                        className={`ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full ${
                          isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {sub.count}
                      </span>
                    </button>
                  );
                })}

                {/* Luk-knap til at gemme boksen væk igen */}
                <button
                  type="button"
                  onClick={() => setShowSubCategories(false)}
                  className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-xs text-gray-500 hover:text-gray-800 hover:bg-white rounded-lg transition-colors cursor-pointer"
                  title="Skjul underkategorier"
                >
                  <i className="fa-solid fa-chevron-up text-[10px]" />
                  <span>Skjul</span>
                </button>
              </div>
            )}
      </div>

      {/* 2. Den aflange kasse / horisontal scrollable række */}
      <div className="relative rounded-3xl border border-gray-200/90 bg-white p-4 sm:p-6 shadow-sm overflow-hidden">
        {/* Header information i den aflange kasse */}
        <div className="relative z-10 flex items-center justify-between pb-4 border-b border-gray-200 text-gray-700 text-xs">
          <div className="flex items-center gap-2 font-medium">
            <span className="h-2 w-2 rounded-full bg-[#325d52] animate-pulse" />
            <span>
              Viser {filteredProjects.length} billeder
              {activePage !== 'alle' ? ` • ${MAIN_PAGES.find((p) => p.id === activePage)?.title || ''}` : ''}
              {activeSubCategory !== 'alle'
                ? ` › ${availableSubCategories.find((s) => s.slug === activeSubCategory)?.label || ''}`
                : ''}{' '}
              (nyeste først) &bull; Klik på et billede for at forstørre
            </span>
          </div>
          <div className="hidden md:flex items-center gap-2 text-gray-500">
            <i className="fa-solid fa-arrows-left-right text-xs" />
            <span>Træk eller scroll for at udforske</span>
          </div>
        </div>

        {/* Billedstrøm (horisontal rullebånd) */}
        <div
          ref={scrollContainerRef}
          onMouseDown={handleMouseDown}
          className={`relative z-10 mt-4 flex gap-4 sm:gap-5 overflow-x-auto pb-3 pt-1 select-none focus:outline-none ${
            isPointerDown ? 'cursor-grabbing select-none' : 'cursor-grab'
          }`}
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(50, 93, 82, 0.4) transparent',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {filteredProjects.map((item, idx) => (
            <div
              key={item.id}
              onClick={(e) => {
                // Kun åbn lightbox hvis musen ikke blev trukket
                if (dragDistanceRef.current > 12) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                setLightboxIndex(idx);
              }}
              className="group relative flex-none w-[260px] sm:w-[320px] md:w-[360px] aspect-[4/3] rounded-2xl overflow-hidden border border-gray-200 bg-gray-100 shadow-sm transition-all duration-300 hover:scale-[1.02] hover:border-[#325d52] hover:shadow-xl select-none cursor-pointer"
            >
              <img
                src={getOptimizedImageUrl(item.imageUrl, 700)}
                alt={`${item.categoryTitle} projekt`}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-108 pointer-events-none select-none"
                loading="lazy"
                draggable={false}
              />

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950/90 via-gray-950/30 to-transparent opacity-75 group-hover:opacity-90 transition-opacity" />

              {/* Badge for album med flere billeder */}
              {item.imageCount > 1 && (
                <div className="absolute top-3 left-3 z-10">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-black/75 px-2.5 py-0.5 text-xs font-semibold text-white backdrop-blur-md border border-white/20 shadow-md">
                    <i className="fa-solid fa-layer-group text-[#deecd4] text-[10px]" />
                    <span>{item.imageCount} billeder</span>
                  </span>
                </div>
              )}

              {/* Hover Forstørrelses-Ikon */}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#deecd4] text-[#1b453f] shadow-md">
                  <i className="fa-solid fa-folder-open text-xs" />
                </span>
              </div>

              {/* Bund Tekst på billedet */}
              <div className="absolute bottom-0 inset-x-0 p-4 text-white transform transition-transform duration-300">
                <p className="text-xs font-semibold text-[#deecd4] flex items-center gap-1.5">
                  <i className="fa-solid fa-camera text-[10px]" />
                  {item.badgeText || 'Se projektbillede'}
                </p>
                <h4 className="text-sm sm:text-base font-bold text-white line-clamp-1 group-hover:text-[#deecd4] transition-colors">
                  {item.imageTitle || item.galleryTitle}
                </h4>
                {item.imageDescription && (
                  <p className="text-xs text-gray-200 line-clamp-1 opacity-90 mt-0.5">
                    {item.imageDescription}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Mobil Scroll Knapper */}
        <div className="flex sm:hidden items-center justify-center gap-3 pt-3 border-t border-gray-200 mt-3">
          <button
            type="button"
            onClick={() => scroll('left')}
            className="flex-1 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
          >
            <i className="fa-solid fa-chevron-left text-[10px]" /> Forrige
          </button>
          <button
            type="button"
            onClick={() => scroll('right')}
            className="flex-1 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
          >
            Næste <i className="fa-solid fa-chevron-right text-[10px]" />
          </button>
        </div>
      </div>

      {/* 3. Album Viewer Modal */}
      <AlbumModal
        isOpen={lightboxIndex !== null}
        album={
          lightboxIndex !== null && filteredProjects[lightboxIndex]
            ? {
                ...filteredProjects[lightboxIndex],
                title: filteredProjects[lightboxIndex].imageTitle || filteredProjects[lightboxIndex].galleryTitle || filteredProjects[lightboxIndex].title,
                description: filteredProjects[lightboxIndex].imageDescription || filteredProjects[lightboxIndex].description,
                category: filteredProjects[lightboxIndex].categoryTitle,
                url: filteredProjects[lightboxIndex].imageUrl || filteredProjects[lightboxIndex].url,
                images: filteredProjects[lightboxIndex].images && filteredProjects[lightboxIndex].images.length > 0
                  ? filteredProjects[lightboxIndex].images
                  : [filteredProjects[lightboxIndex].imageUrl || filteredProjects[lightboxIndex].url]
              }
            : null
        }
        onClose={() => setLightboxIndex(null)}
        onPrevAlbum={
          lightboxIndex !== null && lightboxIndex > 0
            ? () => setLightboxIndex(lightboxIndex - 1)
            : null
        }
        onNextAlbum={
          lightboxIndex !== null && lightboxIndex < filteredProjects.length - 1
            ? () => setLightboxIndex(lightboxIndex + 1)
            : null
        }
        prevAlbumTitle={
          lightboxIndex !== null && lightboxIndex > 0
            ? filteredProjects[lightboxIndex - 1]?.imageTitle || filteredProjects[lightboxIndex - 1]?.galleryTitle
            : ''
        }
        nextAlbumTitle={
          lightboxIndex !== null && lightboxIndex < filteredProjects.length - 1
            ? filteredProjects[lightboxIndex + 1]?.imageTitle || filteredProjects[lightboxIndex + 1]?.galleryTitle
            : ''
        }
      />
    </div>
  );
}
